// NPM packages
import React from 'react';
import { Alert, AlertTitle, Typography } from '@mui/material';
import styled from '@emotion/styled';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';

// All other imports
import PageContent from 'components/core/PageContent';
import TextLink from 'components/core/TextLink';
import H1 from 'components/core/H1';
import CollapsibleSection from 'components/core/CollapsibleSection';
import CopyCode from 'components/core/CopyCode';
import { useServices } from 'services';
import HorizontallyCentered from 'components/core/HorizontallyCentered';
import CircularProgress from 'components/core/CircularProgress';

const Root = styled(PageContent)({
  'h2, h3': {
    marginTop: '1.8rem !important',
  },
  '.fullwidth': {
    width: '100%',
  },
});

const Warning = styled.div({
  '.MuiAlert-icon': {
    fontSize: '29px',
  },
  '.MuiAlertTitle-root': {
    fontSize: '1.8rem !important',
  },
});

const TitleLine = styled.span({
  fontWeight: 'bold',
  marginRight: '5px',
});

const Indent = styled.span({
  display: 'block',
  marginLeft: '20px',
});

const Bold = styled(Typography)({
  fontWeight: 'bold',
  marginTop: '10px',
});

const AnchorLink = styled.a({
  fontWeight: 'bold',
  margin: 0,
  color: '#1b75bb',
  textDecoration: 'none',
});

const Parameter = styled.code({
  fontWeight: 'bold',
  color: 'teal',
  marginRight: '10px',
});

export interface CLIHelpProps {
  className?: string;
}

export default function CLIHelp(
  props: CLIHelpProps
): React.ReactElement | null {
  const { className } = props;
  const { cliService } = useServices();
  const example1 = `# This is a basic example of a filter-file.yaml

# Provide a list of patterns to exclude.  
exclude:
  - '*'   # Exclude all files

# Provide a list of patterns that are exceptions to the 
# above exclude rules.  
except:
  - '*.txt'  # Except, include all files that end with .txt 
`;

  const example2 = `# Exclude all .vscode, .history, and .DS_Store folders and their children
exclude:
  - '*.vscode/*'
  - '*.history/*'
  - '*.DS_Store/*'    
`;
  const example3 = `# Exclude all *.log and *.env files
exclude:
  - '*.log'
  - '*.env'
`;
  const example4 = `# Only include the README.md file
exclude:
  - '*'
except:
  - 'README.md' 
`;
  const example5 = `# Exclude all files that start with the letter a, b, c, or d
exclude:
  - '[abcd]*'
`;

  return (
    <Root className={className}>
      <H1>The MSD-LIVE Command-line Interface (CLI)</H1>
      <Typography>
        Instead of uploading or downloading files through the MSD-LIVE web
        application, you can use the MSD-LIVE Command Line Interface (CLI) to
        upload or download files from a computer terminal. The CLI does not
        require a web browser, and it provides better upload and download
        performance, so it is <strong>recommended for files that are</strong>:
      </Typography>
      <ul>
        <li>Large (in size or quantity)</li>
        <li>Remote (reside on another computer lacking a web browser)</li>
      </ul>
      <Warning>
        <Alert
          severity="warning"
          style={{ fontSize: '1.2rem', marginBottom: '1.2rem' }}
        >
          <AlertTitle style={{ marginBottom: '.8rem' }}>Important</AlertTitle>
          <Typography>
            <TitleLine>Python Required.</TitleLine>The MSD-LIVE CLI requires
            Python 3.8 or higher. If you get a “command not found” error when
            trying to run pip, then you must install Python (or use Conda if you
            have experience with it). Click here to{' '}
            <TextLink to="https://www.python.org/downloads/">
              download the Python Installer
            </TextLink>
            .
          </Typography>
          <Typography>
            <TitleLine>Virtual Environment Recommended.</TitleLine>If you
            encounter dependency conflicts when installing, we recommend using a
            Python virtual environment such as venv or conda. Click here for{' '}
            <AnchorLink href="#using-a-venv">
              more information on using a virtual environment
            </AnchorLink>
            .
          </Typography>
          <Typography>
            <TitleLine>Working Behind a Proxy Server.</TitleLine>If your
            organization uses a firewall or VPN with a web proxy, you will need
            to set certain environment variables in order to install and run the
            CLI. Click here to{' '}
            <AnchorLink href="#running-behind-proxy">
              learn how to run the CLI with a proxy
            </AnchorLink>
            .
          </Typography>
          <Typography>
            <TitleLine>Contact Us.</TitleLine>If you run into any problems
            installing or using the CLI, please contact us at{' '}
            <TextLink to="mailto:info@msdlive.org">info@msdlive.org</TextLink>.
          </Typography>
        </Alert>
      </Warning>
      <CollapsibleSection title="Using a Virtual Environment" id="using-a-venv">
        <Typography id="using-a-venv">
          A Python virtual environment is a self-contained directory that
          contains a specific Python installation along with its own set of
          libraries and dependencies. It allows you to create an isolated
          environment where you can install packages and run Python programs
          without affecting the global Python installation on your system.{' '}
          <strong>
            Using a virtual environment avoids conflicts between different
            Python projects on your system.
          </strong>
        </Typography>

        <Typography variant="h4" component="h3">
          Creating a Virtual Environment
        </Typography>
        <Typography>
          Python provides a built-in module called venv to create and manage
          virtual environments. The steps to create a virtual environment depend
          upon the operating system you are using:
        </Typography>
        <Typography variant="h6" component="h4">
          Windows
        </Typography>
        <ol>
          <li>Open the command prompt.</li>
          <li>
            Enter the following command:{' '}
            <CopyCode code="python -m venv %HOMEPATH%\venvs\msdlive" />
          </li>
        </ol>

        <Typography variant="h6" component="h4">
          Unix-based systems (Linux/macOS)
        </Typography>
        <ol>
          <li>Open a terminal.</li>
          <li>
            Enter the following command:{' '}
            <CopyCode code="python3 -m venv ~/venvs/msdlive" />
          </li>
        </ol>

        <Typography variant="h4" component="h3">
          Activating a Virtual Environment
        </Typography>
        <Typography>
          Once you have created a virtual environment, you need to activate it
          in order to use it. The steps depend upon the operating system you are
          using:
        </Typography>

        <Typography variant="h6" component="h4">
          Windows
        </Typography>
        <ol>
          <li>Open the command prompt.</li>
          <li>
            Enter the following command:{' '}
            <CopyCode code="%HOMEPATH%\venvs\msdlive\Scripts\activate" />
          </li>
        </ol>
        <Typography variant="h6" component="h4">
          Unix-based systems (Linux/macOS)
        </Typography>
        <ol>
          <li>Open a terminal.</li>
          <li>
            Enter the following command:{' '}
            <CopyCode code="source ~/venvs/msdlive/bin/activate" />
          </li>
        </ol>

        <Typography>
          After executing the appropriate command, you should see that the
          virtual environment is activated. The prompt in your command prompt or
          terminal will change to indicate that you are now working within the
          virtual environment. You can then install packages or run Python
          scripts within this activated venv.
        </Typography>

        <Typography variant="h4">Deactivating a Virtual Environment</Typography>

        <Typography>
          Once you have finished working in your virtual environment, you can
          deactivate it to restore your system's default Python environment:
        </Typography>

        <ol>
          <li>Open a terminal (Linux/macOS) or command prompt (Windows)</li>
          <li>Enter the following command:</li>
          <CopyCode code="deactivate" />
        </ol>

        <Typography>
          After executing the command, the virtual environment will be
          deactivated. The prompt will no longer display the venv name,
          indicating that you have returned to your system's default Python
          environment.
        </Typography>
      </CollapsibleSection>

      <CollapsibleSection title="Installing the CLI" id="install-cli">
        <Typography>
          To install the CLI, run this command from your computer terminal:
        </Typography>
        {cliService.installCmd ? (
          <CopyCode code={cliService.installCmd} />
        ) : (
          <HorizontallyCentered>
            <CircularProgress />
          </HorizontallyCentered>
        )}
        <Alert
          severity="info"
          style={{ marginBottom: '1rem', marginTop: '1rem' }}
        >
          <AlertTitle style={{ marginBottom: '.8rem' }}>Note</AlertTitle>
          <Typography>
            If you get an error that pip is not found, try replacing
            <span style={{ display: 'block' }}>
              <b>
                <code>pip install</code>
              </b>
            </span>
            with
            <span style={{ display: 'block' }}>
              <b>
                <code>python3 -m pip install </code>
              </b>
            </span>
          </Typography>
        </Alert>
      </CollapsibleSection>

      <CollapsibleSection title="Using the CLI" id="using-the-cli">
        <Typography>
          The MSD-LIVE CLI provides easy methods to help you upload or download
          files directly to or from a dataset.
        </Typography>

        <Typography variant="h3">Getting Help</Typography>
        <Typography>
          If you can't remember the command syntax, use the built-in help to get
          the list of commands that are available.
        </Typography>

        <CopyCode code="msdlive --help" />

        <Typography>
          To get a description of a specific command and its required and
          optional parameters, run the following:
        </Typography>
        <CopyCode code="msdlive $COMMAND_NAME --help" />

        <Typography>Example:</Typography>
        <CopyCode code="msdlive download --help" />

        <Typography variant="h3">Logging In</Typography>
        <Typography>
          Before you can upload any files to your draft dataset or download
          files from a protected dataset, you must login first:
        </Typography>
        <CopyCode code="msdlive login" />
        <Typography>
          Once logged in, your credentials will last for two days. If you want
          to continue using the CLI after that time, you will need to log in
          again.
        </Typography>

        <Typography variant="h3">Downloading Files</Typography>
        <Typography>
          The msdlive download command allows you to download one or or more
          files from a given dataset. The general syntax is as follows:
        </Typography>
        <CopyCode code="msdlive download --dataset-id $YOUR_DATASET_ID --output-dir $YOUR_LOCAL_DIRECTORY" />

        <Indent>
          <Bold>Required Parameters:</Bold>
          <Indent>
            <Typography>
              <TitleLine>
                <Parameter>--dataset-id $YOUR_DATASET_ID</Parameter>
              </TitleLine>
              Replace $YOUR_DATASET_ID with the dataset id that you get from the
              MSD-LIVE Data Repository web interface. From your dataset's web
              page, click the 'Download files from the command line...' link to
              get the exact command which includes the dataset id.
            </Typography>
            <Typography>
              <TitleLine>
                <Parameter>--output-dir $YOUR_LOCAL_DIRECTORY</Parameter>
              </TitleLine>
              Replace $YOUR_LOCAL_DIRECTORY with the path to a folder on your
              local computer. All the files from the dataset will be downloaded
              to this folder
            </Typography>
          </Indent>
        </Indent>
        <Indent>
          <Bold>Optional Parameters:</Bold>
          <Indent>
            <Typography>
              <TitleLine>
                <Parameter>--filter-file $PATH_TO_FILE</Parameter>
              </TitleLine>
              If you only want to download some files, add the --filter-file
              parameter, followed by the path to a 'filter' file. The filter
              file contains special syntax describing which files to exclude and
              which to include. See the{' '}
              <AnchorLink href="#filter-files">
                Filtering Files Section
              </AnchorLink>{' '}
              for details on the file syntax.
            </Typography>
            <Typography>
              <TitleLine>
                <Parameter>--quiet</Parameter>
              </TitleLine>
              When you include the --quiet parameter, it suppresses the standard
              output and only displays error messages. This can be helpful if
              you want to run the synchronization process silently without being
              overwhelmed by the detailed progress information.
            </Typography>
          </Indent>
        </Indent>

        <Typography variant="h3">Uploading Files</Typography>
        <Typography>
          The msdlive upload command allows you to upload a file or folder to a
          given dataset. The general syntax is as follows:
        </Typography>
        <CopyCode code="msdlive upload --dataset-id $YOUR_DATASET_ID --src-dir $PATH_TO_LOCAL_FOLDER" />

        <Indent>
          <Bold>Required Parameters:</Bold>
          <Indent>
            <Typography>
              <TitleLine>
                <Parameter>--dataset-id $YOUR_DATASET_ID</Parameter>
              </TitleLine>
              Replace $YOUR_DATASET_ID with the dataset id that you get from the
              MSD-LIVE Data Repository web interface. From your dataset's web
              page, click the 'Upload files from the command line...' link to
              get the exact command which includes the dataset id.
            </Typography>
            <Typography>
              <TitleLine>
                <Parameter>--src-dir $PATH_TO_LOCAL_FOLDER</Parameter>
              </TitleLine>
              The --src-dir parameter specifies the folder you want to upload.
              Replace $PATH_TO_LOCAL_FOLDER with the path to a folder on your
              local computer. By default, all the contents of that folder will
              be uploaded to your dataset. If you only want to upload certain
              files, use the optional parameter --filter-file. See the{' '}
              <AnchorLink href="#filter-files">
                Filtering Files Section
              </AnchorLink>{' '}
              for details on the file syntax.
            </Typography>
          </Indent>
        </Indent>
        <Indent>
          <Bold>Optional Parameters:</Bold>
          <Indent>
            <Typography>
              <TitleLine>
                <Parameter>--filter-file $PATH_TO_FILE</Parameter>
              </TitleLine>
              If you only want to upload certain files from the source
              directory, add the --filter-file parameter. Replace $PATH_TO_FILE
              with the path to a file on your local computer. The filter file
              contains special syntax describing which files to exclude and
              which to include. See the{' '}
              <TextLink to="#filter-files">Filtering Files Section</TextLink>{' '}
              for details on the file syntax.
            </Typography>
            <Typography>
              <TitleLine>
                <Parameter>--quiet</Parameter>
              </TitleLine>
              When you include the --quiet parameter, it suppresses the standard
              output and only displays error messages. This can be helpful if
              you want to run the synchronization process silently without being
              overwhelmed by the detailed progress information.
            </Typography>
            <Typography>
              <TitleLine>
                <Parameter>--delete</Parameter>
              </TitleLine>
              If you upload the same directory to your dataset more than once,
              the command will compare files in your dataset with those in your
              source folder and only upload the new or modified files to reduce
              the amount of data transferred. By default, files that have been
              deleted from your source folder will not be deleted in the remote
              dataset. If the --delete option is specified, then the command
              also deletes any files from your dataset that no longer exist in
              your source folder.
            </Typography>
          </Indent>
        </Indent>

        <Typography id="filter-files" variant="h3">
          Filtering Files
        </Typography>
        <Typography>
          By default, all files are included in an msdlive upload or download.
          If you want to exclude or include only certain files, you will need to
          create a filter file and then pass the path to that file on the
          command line using the <code>--filter-file</code> parameter.
        </Typography>
        <Typography>
          The filter file is a{' '}
          <TextLink to="https://www.cloudbees.com/blog/yaml-tutorial-everything-you-need-get-started">
            YAML{' '}
          </TextLink>{' '}
          file that has two sections, one for files to exclude and one for
          exceptions to the exclude rules, as shown in this example:
        </Typography>

        <SyntaxHighlighter language="yaml" style={docco}>
          {example1}
        </SyntaxHighlighter>

        <Alert severity="info" style={{ marginBottom: '1.2rem' }}>
          <AlertTitle style={{ marginBottom: '.8rem' }}>Note</AlertTitle>
          <ul style={{ paddingLeft: '20px' }}>
            <li>
              We recommend using an editor such as VSCode that supports{' '}
              <TextLink to="https://www.cloudbees.com/blog/yaml-tutorial-everything-you-need-get-started">
                YAML file syntax
              </TextLink>{' '}
              to make creating your filter file easier.
            </li>
            <li>
              If no excludes are listed, then all files will be included by
              default, no matter what is in the except section!
            </li>
            <li>
              If no excludes are listed, then the except section will be
              ignored!
            </li>
          </ul>
        </Alert>

        <Typography>
          Each section contains a list of patterns that identify a subset of
          files or folders. The patterns can include wildcards and other special
          characters to match multiple files or directories. Here are some
          important aspects of the file format:
        </Typography>
        <ol>
          <li>
            <TitleLine>Blank lines and comments:</TitleLine>
            Blank lines and lines starting with a hash symbol (#) are ignored
            and treated as comments.
          </li>
          <li>
            <TitleLine>Pattern matching:</TitleLine>
            Patterns can use wildcards to match multiple files or directories.
            The most commonly used wildcards are:
            <ul>
              <li>*: Matches everything</li>
              <li>?: Matches any single character</li>
              <li>[sequence]: Matches any character in sequence</li>
              <li>[!sequence]: Matches any character not in sequence</li>
            </ul>
          </li>
          <li>
            <TitleLine>Directory matching:</TitleLine>
            To specify an entire directory, add the relative path to the
            directory followed a trailing slash and an asterisk (/*). For
            example, <b>mydirectory/*</b> will indicate the entire "mydirectory"
            directory.
          </li>
          <li>
            <TitleLine>Negation:</TitleLine>
            Any file patterns listed in the <b>except</b> section will reverse
            the general exclusions from the <b>exclude</b> section. This is
            useful if you want to exclude a directory but include a specific
            file or files within it.
          </li>
          <li>
            <TitleLine>Relative paths:</TitleLine>
            Patterns in the file are relative to the location of the source
            directory.
          </li>
        </ol>

        <Typography variant="h4" component="h3">
          Examples
        </Typography>
        <SyntaxHighlighter language="yaml" style={docco}>
          {example2}
        </SyntaxHighlighter>

        <SyntaxHighlighter language="yaml" style={docco}>
          {example3}
        </SyntaxHighlighter>

        <SyntaxHighlighter language="yaml" style={docco}>
          {example4}
        </SyntaxHighlighter>

        <SyntaxHighlighter language="yaml" style={docco}>
          {example5}
        </SyntaxHighlighter>

        <Typography variant="h3">Logging Out</Typography>
        <Typography>
          Your msdlive credentials will automatically expire after two days of
          inactivity, so there is no need to log out, but if you wish to expire
          the credentials earlier, you can run the logout command:
        </Typography>
        <CopyCode code="msdlive logout" />
      </CollapsibleSection>

      <CollapsibleSection
        title="Running the CLI Behind a Proxy"
        id="running-behind-proxy"
      >
        <Typography>
          If you get certificate errors when trying to install or run the CLI,
          it is likely that your organization is using a web proxy. In order to
          work with a web proxy, you need to tell your applications that all
          traffic is being routed through a proxy. Different applications have
          different ways to communicate this information. Below we show you the
          steps needed to install the CLI and run commands.
        </Typography>

        <Typography variant="h4" component="h3">
          Installing the CLI Behind a Proxy
        </Typography>
        <Typography>
          From your activated virtual environment shell, run this command:
        </Typography>
        <CopyCode code="pip install --proxy $PROXY_URL https://github.com/MSD-LIVE/msdlive-cli-distro/raw/dev/dist/msdlive_cli-0.3.6-py3-none-any.whl" />

        <Typography variant="h4" component="h3">
          Running an MSD-LIVE CLI command Behind a Proxy
        </Typography>
        <Typography>
          From your activated virtual environment shell, run this command:
        </Typography>

        <Typography variant="h6" component="h4">
          Windows
        </Typography>
        <CopyCode code="set HTTPS_PROXY=YOUR_PROXY_URL_GOES_HERE" />

        <Typography variant="h6" component="h4">
          Linux/macOS:
        </Typography>
        <CopyCode code="export HTTPS_PROXY=YOUR_PROXY_URL_GOES_HERE" />

        <ul>
          <li>
            Replace <b>YOUR_PROXY_URL_GOES_HERE</b> with the proxy URL for your
            organization. Contact your system administrator for this value.
          </li>
          <li>
            Once this environment variable is set, then you can safely run any
            msdlive command.
          </li>
        </ul>
      </CollapsibleSection>
    </Root>
  );
}
